import { Info, MoreVertical } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { AssignmentListRow } from './AssignmentContainer';
import {
  ICS_204_CG_FORM_NAME,
  useArchiveWorkAssignmentListAttachment,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import {
  useIcsFormAvailability,
  useIcsFormFamily,
} from 'components/IAP/hooks/useIcsFormFamily';
import { SectionChiefInfo } from './useSectionChiefResolver';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import ChangeLogDialog from './ChangeLogDialog';
import AssignmentCardSpecialInstructions from './AssignmentCardSpecialInstructions';
import CommunicationsSection from './CommunicationsSection';
import { ICS204_CONTENT, ICS204_ICON_BTN } from './assignmentCardStyles';
import { Ics204ReadOnlyField } from './Ics204Fields';
import { ICS204_BOX_LABELS, Ics204CardSection } from './ics204BoxLabels';
import type { Ics204EditingSection } from './ics204Sections';
import { renderIcs204SectionEditor } from './Ics204SectionEditors';
import { Ics204IncidentPeriodHeader } from './Ics204IncidentPeriodHeader';
import { PersonnelSectionTableView } from './PersonnelSectionTable';
import { PERSONNEL_ROLES } from './personnelSection';
import {
  ICS204_ASSIGN_HANDOFF_TOOLTIP,
  ICS204_ASSIGN_NEEDS_IAP,
  getIcs204AddDisabledReason,
} from './assignmentStatus';
import { useIapCoverApproved } from './useIapCoverApproved';
import Ics204VersioningPanel from './Ics204VersioningPanel';
import {
  isVersionFullySigned,
  parseWalaSignatures,
} from './ics204SignatureSlots';
import type { WalaVersionRow } from './Ics204VersionsDialog';
import ResourcesAssignedSection from './ResourcesAssignedSection';
import WorkAssignmentsSection from './WorkAssignmentsSection';

type AssignmentRowHeaderActionsProps = {
  item: AssignmentListRow;
  isCurrentUserReviewer: boolean;
  periodNumber: number;
  readOnly?: boolean;
};

/** Kebab (Assign / Preview / Change Log / Delete). Section edit is per-box. */
export const AssignmentRowHeaderActions = ({
  item,
  isCurrentUserReviewer,
  periodNumber,
  readOnly = false,
}: AssignmentRowHeaderActionsProps) => {
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();
  const { mutate: archive, isPending: isArchiving } =
    useArchiveWorkAssignmentListAttachment();
  const { exportWorkAssignmentListAttachment } = useIcsFormPdfPreview();
  const { supportsDocx } = useIcsFormAvailability(ICS_204_CG_FORM_NAME);
  const formFamily = useIcsFormFamily();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showChangeLog, setShowChangeLog] = useState(false);

  const fullySigned = isVersionFullySigned(
    parseWalaSignatures(item.signatures)
  );
  const iapApproved = useIapCoverApproved(periodNumber);
  const canAssign =
    isCurrentUserReviewer &&
    item.actualStatus === 'draft' &&
    item.walaId !== null &&
    fullySigned &&
    iapApproved;
  const assignBlockedReason = !fullySigned
    ? 'This ICS-204 must be fully signed (Prepared By, PSC, and OSC) before it can be assigned.'
    : ICS204_ASSIGN_NEEDS_IAP;

  const assignLabel = `Assign to ${item.workGroup || 'work group'}`;

  const assign = () => {
    if (item.walaId) {
      update({ walaId: item.walaId, proposedStatus: 'in_progress' });
    }
  };

  return (
    <>
      <div className="tw:flex tw:min-w-0 tw:flex-nowrap tw:items-center tw:justify-end tw:gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Open row actions"
                className={ICS204_ICON_BTN}
                data-testid="ics204-row-menu-button"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="tw:h-4 tw:w-4" />
              </Button>
            }
          />
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="dark"
            onClick={(e) => e.stopPropagation()}
          >
            {isCurrentUserReviewer &&
              item.actualStatus === 'draft' &&
              item.walaId !== null && (
                <DropdownMenuItem
                  data-testid="ics204-assign-button"
                  className="tw:justify-between tw:gap-2"
                  disabled={!canAssign}
                  onClick={() => {
                    if (canAssign) assign();
                  }}
                >
                  <span className="tw:truncate">{assignLabel}</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="What assigning to a work group does"
                            className="tw:inline-flex tw:shrink-0"
                            data-testid="ics204-assign-info-button"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <Info className="tw:h-4 tw:w-4" />
                          </span>
                        }
                      />
                      <TooltipContent side="left" className="tw:max-w-xs">
                        {canAssign
                          ? ICS204_ASSIGN_HANDOFF_TOOLTIP
                          : assignBlockedReason}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </DropdownMenuItem>
              )}
            {formFamily !== 'fema' && (
              <DropdownMenuItem
                data-testid="ics204-preview-button"
                disabled={item.walaId === null}
                onClick={() => {
                  if (item.walaId) {
                    void exportWorkAssignmentListAttachment(item.walaId, 'pdf');
                  }
                }}
              >
                Preview PDF
              </DropdownMenuItem>
            )}
            {supportsDocx && (
              <DropdownMenuItem
                data-testid="ics204-preview-word-button"
                disabled={item.walaId === null}
                onClick={() => {
                  if (item.walaId) {
                    void exportWorkAssignmentListAttachment(
                      item.walaId,
                      'docx'
                    );
                  }
                }}
              >
                Preview Word
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              data-testid="ics204-change-log-button"
              disabled={item.walaId === null}
              onClick={() => {
                if (item.walaId) {
                  setShowChangeLog(true);
                }
              }}
            >
              Change Log
            </DropdownMenuItem>
            {!readOnly && item.walaId !== null && (
              <DropdownMenuItem
                variant="destructive"
                data-testid="ics204-delete-button"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent
          className="dark"
          data-testid="ics204-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this assignment card?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the ICS-204 for {item.workGroup || 'this work group'}{' '}
              from the Assignment List. It can be recreated later from the 215
              or from scratch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ics204-delete-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ics204-delete-confirm-accept"
              disabled={isArchiving}
              onClick={() => {
                if (item.walaId) {
                  archive(item.walaId);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {showChangeLog && item.walaId !== null && (
        <ChangeLogDialog
          onClose={() => setShowChangeLog(false)}
          walaId={item.walaId}
        />
      )}
    </>
  );
};

type AssignmentCardViewProps = {
  item: AssignmentListRow;
  /**
   * Tip row (latest) — used for signature/version actions even while viewing
   * past.
   */
  tipItem: AssignmentListRow;
  sectionChief?: SectionChiefInfo;
  periodNumber: number;
  readOnly?: boolean;
  contentLocked?: boolean;
  editingSection: Ics204EditingSection | null;
  onEditSection: (section: Ics204EditingSection) => void;
  onCancelSectionEdit: () => void;
  isCreatingSignedVersion: boolean;
  viewingVersionId: string | null;
  onStartCreateSignedVersion: () => void;
  onCancelCreateSignedVersion: () => void;
  onSignedVersionCreated: () => void;
  onUnsignedVersionCreated: () => void;
  onViewVersion: (version: WalaVersionRow) => void;
  onViewLatest: () => void;
};

const isPortaledUiTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    [
      '[data-slot="select-content"]',
      '[data-slot="dropdown-menu-content"]',
      '[data-slot="popover-content"]',
      '[data-slot="dialog-content"]',
      '[data-slot="alert-dialog-content"]',
      '[data-slot="tooltip-content"]',
      '[role="listbox"]',
      '[data-radix-popper-content-wrapper]',
    ].join(', ')
  );
};

/** Expanded ICS-204 body (sections 3–8 + per-section edit + workflow buttons). */
const AssignmentCardView = ({
  item,
  tipItem,
  sectionChief,
  periodNumber,
  readOnly = false,
  contentLocked = false,
  editingSection,
  onEditSection,
  onCancelSectionEdit,
  isCreatingSignedVersion,
  viewingVersionId,
  onStartCreateSignedVersion,
  onCancelCreateSignedVersion,
  onSignedVersionCreated,
  onUnsignedVersionCreated,
  onViewVersion,
  onViewLatest,
}: AssignmentCardViewProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const pendingSectionRef = useRef<Ics204EditingSection | null>(null);

  const canEditSections =
    !readOnly &&
    !contentLocked &&
    !isCreatingSignedVersion &&
    tipItem.walaId !== null &&
    tipItem.workGroup !== '' &&
    tipItem.actualStatus !== 'awaiting_review';

  const addDisabledReason = getIcs204AddDisabledReason({
    viewingVersionId,
    contentLocked,
    readOnly,
    isCreatingSignedVersion,
    walaId: tipItem.walaId,
    workGroup: tipItem.workGroup,
    actualStatus: tipItem.actualStatus,
  });

  const submitEditingSection = () => {
    const form = contentRef.current?.querySelector<HTMLFormElement>(
      '[data-testid^="ics204-section-edit-"]'
    );
    form?.requestSubmit();
  };

  const activateSection = (section: Ics204EditingSection) => {
    if (!canEditSections) return;
    if (editingSection === section) return;
    if (editingSection) {
      pendingSectionRef.current = section;
      submitEditingSection();
      return;
    }
    onEditSection(section);
  };

  const handleSectionSaved = () => {
    const pending = pendingSectionRef.current;
    pendingSectionRef.current = null;
    onCancelSectionEdit();
    if (pending) {
      queueMicrotask(() => onEditSection(pending));
    }
  };

  const handleSectionDiscard = () => {
    pendingSectionRef.current = null;
    onCancelSectionEdit();
  };

  useEffect(() => {
    if (!editingSection) return;

    const onPointerDown = (event: PointerEvent) => {
      if (isPortaledUiTarget(event.target)) return;
      const editingRoot = contentRef.current?.querySelector(
        '[data-ics204-section-editing="true"]'
      );
      if (!editingRoot) return;
      if (event.target instanceof Node && editingRoot.contains(event.target)) {
        return;
      }
      submitEditingSection();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [editingSection]);

  const sectionBody = (
    section: Ics204EditingSection,
    view: ReactNode
  ): ReactNode => {
    if (editingSection === section) {
      return renderIcs204SectionEditor(section, {
        item: tipItem,
        periodNumber,
        onDone: handleSectionSaved,
        onCancel: handleSectionDiscard,
      });
    }
    return view;
  };

  const sectionActivateProps = (section: Ics204EditingSection) => ({
    isEditing: editingSection === section,
    onSectionActivate: canEditSections
      ? () => activateSection(section)
      : undefined,
  });

  return (
    <Ics204VersioningPanel
      walaId={tipItem.walaId}
      tipId={tipItem.id}
      tipSignatures={parseWalaSignatures(tipItem.signatures)}
      tipUpdatedAt={tipItem.updatedAt}
      tipUpdatedByUserName={tipItem.updatedByUserName}
      isCreatingSignedVersion={isCreatingSignedVersion}
      viewingVersionId={viewingVersionId}
      onStartCreateSignedVersion={onStartCreateSignedVersion}
      onCancelCreateSignedVersion={onCancelCreateSignedVersion}
      onSignedVersionCreated={onSignedVersionCreated}
      onUnsignedVersionCreated={onUnsignedVersionCreated}
      onViewVersion={onViewVersion}
      onViewLatest={onViewLatest}
    >
      <div
        ref={contentRef}
        className={cn(ICS204_CONTENT, 'tw:min-w-0')}
        data-testid="ics204-card-content"
      >
        {item.actualStatus === 'awaiting_review' && (
          <Alert className="tw:m-3">
            <AlertDescription>
              This work assignment list attachment is awaiting review. It cannot
              be edited until it is reviewed.
            </AlertDescription>
          </Alert>
        )}
        <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
          <Ics204IncidentPeriodHeader periodNumber={periodNumber} />
          <Ics204CardSection
            boxLabel={ICS204_BOX_LABELS.assignedLocation}
            testId="ics204-section-assigned-location"
            className="tw:mt-0 tw:h-full tw:min-w-0 tw:overflow-hidden"
            {...sectionActivateProps('assignedLocation')}
          >
            {sectionBody(
              'assignedLocation',
              <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
                <Ics204ReadOnlyField
                  label="Branch"
                  value={item.branch}
                  testId="ics204-field-branch"
                  className="tw:min-w-0"
                />
                <Ics204ReadOnlyField
                  label="Division"
                  value={item.division}
                  testId="ics204-field-division"
                  className="tw:min-w-0"
                />
                <Ics204ReadOnlyField
                  label="Group"
                  value={item.walaGroup}
                  testId="ics204-field-group"
                  className="tw:min-w-0"
                />
                <Ics204ReadOnlyField
                  label="Staging Area"
                  value={item.stagingArea}
                  testId="ics204-field-staging-area"
                  className="tw:min-w-0"
                />
              </div>
            )}
          </Ics204CardSection>
        </div>

        <Ics204CardSection
          boxLabel={ICS204_BOX_LABELS.personnel}
          testId="ics204-section-personnel"
          {...sectionActivateProps('personnel')}
        >
          {sectionBody(
            'personnel',
            <PersonnelSectionTableView
              rows={PERSONNEL_ROLES.map((role) => {
                if (role.nameField === 'sectionChief') {
                  return {
                    position: role.position,
                    name: item.sectionChief || sectionChief?.personName || '',
                    contact: item.sectionChiefContact || '',
                    nameTestId: role.viewNameTestId,
                    contactTestId: role.viewContactTestId,
                  };
                }
                if (role.nameField === 'branchDirector') {
                  return {
                    position: role.position,
                    name: item.branchDirector || '',
                    contact: item.branchDirectorContact || '',
                    nameTestId: role.viewNameTestId,
                    contactTestId: role.viewContactTestId,
                  };
                }
                return {
                  position: role.position,
                  name: item.divisionGroupSupervisor || '',
                  contact: item.divisionGroupSupervisorContact || '',
                  nameTestId: role.viewNameTestId,
                  contactTestId: role.viewContactTestId,
                };
              })}
            />
          )}
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS204_BOX_LABELS.resourcesAssigned}
          testId="ics204-section-resources-assigned"
        >
          <ResourcesAssignedSection
            plannedResources={item.plannedResources}
            actualStatus={item.actualStatus}
            walaId={item.walaId}
            periodNumber={periodNumber}
            canEdit={canEditSections}
            disabledReason={addDisabledReason}
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS204_BOX_LABELS.workAssignments}
          testId="ics204-section-work-assignments"
        >
          <WorkAssignmentsSection
            workGroupId={tipItem.workGroupId}
            periodNumber={periodNumber}
            walaId={tipItem.walaId}
            canEdit={canEditSections}
            disabledReason={addDisabledReason}
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS204_BOX_LABELS.specialInstructions}
          testId="ics204-section-special-instructions"
          {...sectionActivateProps('specialInstructions')}
        >
          {sectionBody(
            'specialInstructions',
            <AssignmentCardSpecialInstructions
              specialInstructions={item.specialInstructions || ''}
            />
          )}
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS204_BOX_LABELS.communications}
          testId="ics204-section-communications"
          className="tw:mt-3 tw:mb-3"
        >
          <CommunicationsSection
            workGroupId={tipItem.workGroupId}
            walaId={tipItem.walaId}
            canEdit={canEditSections}
            disabledReason={addDisabledReason}
            medicalCommunication={item.medicalCommunication}
            evacuationCommunication={item.evacuationCommunication}
            otherCommunication={item.otherCommunication}
          />
        </Ics204CardSection>
      </div>
    </Ics204VersioningPanel>
  );
};

export default AssignmentCardView;
